// src/Pages/Landing/JoyrideKaupunkiLanding.jsx
import React, { useEffect } from "react";
import { initAOS } from "../../helpers/aosConfig";
import defaultJoyrideHero from "../../assets/joyride_group_light.webp";

/**
 * JoyrideKaupunkiLanding
 *
 * Yhteinen kaupunkiländäripohja Bilebändi Joyridelle.
 *
 * Ero maakuntapohjaan:
 * - otsikointi kohdistuu kaupunkiin
 * - mukaan voi antaa maakunnan ja lähialueet
 * - tekstissä painotetaan logistiikkaa, lähialuetta ja tapahtumatyyppejä
 * - GA4-eventit erotettu kaupunkiländäreille
 */

const defaultNearbyAreas = ["Vantaa", "Espoo", "Kerava", "Järvenpää"];

const defaultFaqs = [
  {
    question: "Voiko Bilebändi Joyriden tilata kaupunkiin?",
    answer:
      "Kyllä. Bilebändi Joyride tekee keikkoja kaupunkialueille ja lähikuntiin tapahtuman luonteesta, aikataulusta ja matkasta riippuen."
  },
  {
    question: "Tuoko Bilebändi Joyride oman äänentoiston?",
    answer:
      "Kyllä. Bilebändi Joyride tuo tarvittaessa mukanaan oman ääni- ja valotekniikan. Kaluston kasaamiseen ja soundcheckiin kannattaa varata noin kaksi tuntia."
  },
  {
    question: "Mihin juhliin Bilebändi Joyride sopii?",
    answer:
      "Bilebändi Joyride sopii häihin, yritysjuhliin, pikkujouluihin, syntymäpäiville, kesäjuhliin ja muihin tilaisuuksiin, joissa halutaan energinen livebändi."
  },
  {
    question: "Onko tarjouspyyntö sitova?",
    answer:
      "Ei. Pelkkä tiedustelun lähettäminen ei sido tilaajaa. Varaus muuttuu sitovaksi vasta, kun tarjous on hyväksytty ja varausmaksu on maksettu."
  }
];

export default function JoyrideKaupunkiLanding({
  city = "Helsinki",
  maakunta = "Uusimaa",
  nearbyAreas = defaultNearbyAreas,
  cityText = "Bilebändi Joyride keikkailee kaupungissa ja lähialueilla tapahtuman luonteesta, aikataulusta ja matkasta riippuen.",
  logisticsText = "Keski-Uudeltamaalta käsin Bilebändi Joyriden on helppo liikkua pääkaupunkiseudulle ja lähialueille. Oma kuljetus, äänentoisto ja valotekniikka helpottavat kokonaisuuden suunnittelua.",
  bookingLink = "/#varaa",
  heroImage = defaultJoyrideHero
}) {
  const nearbyText = nearbyAreas.join(", ");

  useEffect(() => {
    initAOS();

    // GA4 – käyttäjä avasi Bilebändi Joyriden kaupunkiländärin
    if (window.gaEvent) {
      window.gaEvent("joyride_kaupunkilanderi_katsottu", {
        kaupunki: city
      });
    }
  }, [city]);

  const handleAvailabilityClick = () => {
    // GA4 – käyttäjä klikkasi kaupunkiländärin saatavuus-CTA:ta
    if (window.gaEvent) {
      window.gaEvent("joyride_kaupunkilanderi_saatavuus_klikattu", {
        kaupunki: city
      });
    }
  };

  const handleEmailClick = () => {
    // GA4 – käyttäjä klikkasi kaupunkiländärin sähköpostia
    if (window.gaEvent) {
      window.gaEvent("joyride_kaupunkilanderi_email_klikattu", {
        kaupunki: city
      });
    }
  };

  return (
    <main className="joyride-landing relative overflow-hidden bg-white font-sans text-joyrideNavy dark:bg-joyrideNavy dark:text-joyrideLight">
      {/* Taustakoristeet */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-joyridePurple/20 blur-3xl" />
        <div className="absolute top-64 -right-24 h-80 w-80 rounded-full bg-joyrideCyan/20 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        {/* HERO */}
        <section className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div data-aos="fade-up">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.22em] text-joyridePurple dark:text-joyrideCyan">
              Bilebändi {city}
            </p>

            <h1 className="font-lobster text-4xl font-bold leading-tight text-joyridePurple dark:text-joyrideCyan sm:text-5xl lg:text-6xl">
              Bilebändi Joyride {city} – livebändi häihin, juhliin ja yritystapahtumiin
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-relaxed font-light text-joyrideNavy dark:text-joyrideLight">
              Etsitkö bilebändiä kaupunkiin {city}? Bilebändi Joyride tuo
              juhliin energisen livefiiliksen, tutut coverbiisit, oman
              äänentoiston ja rennon mutta varman esiintymisen.
            </p>

            <p className="mt-4 max-w-2xl text-lg leading-relaxed font-light text-joyrideNavy dark:text-joyrideLight">
              {cityText}
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <a
                href={bookingLink}
                onClick={handleAvailabilityClick}
                className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-base font-bold text-joyridePurple shadow-lg transition hover:-translate-y-0.5 hover:bg-joyrideNavy hover:text-white dark:text-joyrideNavy dark:hover:bg-sky-800 dark:hover:text-white"
              >
                Tarkista saatavuus
              </a>

              <a
                href="mailto:kontakti@bilebandijoyride.com"
                onClick={handleEmailClick}
                className="inline-flex items-center justify-center rounded-full border border-joyridePurple/40 px-7 py-3 text-base font-bold text-joyridePurple transition hover:-translate-y-0.5 hover:bg-joyridePurple hover:text-white dark:border-joyrideCyan/50 dark:text-joyrideCyan dark:hover:bg-joyrideCyan dark:hover:text-joyrideNavy"
              >
                Kysy ennen varausta
              </a>
            </div>

            <p className="mt-5 text-sm leading-relaxed text-joyrideNavy/75 dark:text-joyrideLight/75">
              Lähialueita esimerkiksi: {nearbyText}.
            </p>
          </div>

          <div data-aos="fade-up" data-aos-delay="120">
            <div className="overflow-hidden rounded-3xl border border-joyridePurple/20 bg-white shadow-2xl dark:border-joyrideCyan/20 dark:bg-black/30">
              <img
                src={heroImage}
                alt={`Bilebändi Joyride keikalle kaupunkiin ${city}`}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-2xl border border-joyridePurple/15 bg-white/80 p-4 shadow-md dark:border-joyrideCyan/15 dark:bg-white/10">
                <div className="text-2xl font-bold text-joyridePurple dark:text-joyrideCyan">
                  Oma
                </div>
                <div className="mt-1 text-xs text-joyrideNavy/70 dark:text-joyrideLight/70">
                  kuljetus
                </div>
              </div>

              <div className="rounded-2xl border border-joyridePurple/15 bg-white/80 p-4 shadow-md dark:border-joyrideCyan/15 dark:bg-white/10">
                <div className="text-2xl font-bold text-joyridePurple dark:text-joyrideCyan">
                  2–3
                </div>
                <div className="mt-1 text-xs text-joyrideNavy/70 dark:text-joyrideLight/70">
                  settiä sovitusti
                </div>
              </div>

              <div className="rounded-2xl border border-joyridePurple/15 bg-white/80 p-4 shadow-md dark:border-joyrideCyan/15 dark:bg-white/10">
                <div className="text-2xl font-bold text-joyridePurple dark:text-joyrideCyan">
                  Oma
                </div>
                <div className="mt-1 text-xs text-joyrideNavy/70 dark:text-joyrideLight/70">
                  äänentoisto
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SISÄLTÖ */}
        <section className="mt-20 grid gap-8 lg:grid-cols-2">
          <article
            className="rounded-3xl border border-joyridePurple/15 bg-white/85 p-7 shadow-xl dark:border-joyrideCyan/15 dark:bg-white/10"
            data-aos="fade-up"
          >
            <h2 className="font-lobster text-3xl font-bold text-joyridePurple dark:text-joyrideCyan">
              Livebändi kaupunkiin {city}
            </h2>

            <p className="mt-4 text-lg leading-relaxed font-light">
              Bilebändi Joyride sopii tilanteisiin, joissa juhliin halutaan
              oikea livebändi, mutta kokonaisuuden pitää olla helposti
              sovittava. Bändi tuo mukanaan oman tekniikan, suunnittelee setit
              etukäteen ja sovittaa esiintymisen tilaisuuden luonteeseen.
            </p>

            <p className="mt-4 text-lg leading-relaxed font-light">
              Ohjelmisto koostuu tutuista kotimaisista ja kansainvälisistä
              coverbiiseistä. Tavoitteena on nostaa tunnelmaa, saada juhlaväki
              mukaan ja tehdä illasta elävämpi kuin pelkällä taustamusiikilla.
            </p>
          </article>

          <article
            className="rounded-3xl border border-joyridePurple/15 bg-white/85 p-7 shadow-xl dark:border-joyrideCyan/15 dark:bg-white/10"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="font-lobster text-3xl font-bold text-joyridePurple dark:text-joyrideCyan">
              Miksi Bilebändi Joyride kaupunkiin {city}?
            </h2>

            <ul className="mt-5 space-y-3 text-lg leading-relaxed font-light">
              <li>• Rock-henkinen ja energinen livebändi.</li>
              <li>• Oma ääni- ja valotekniikka mukana.</li>
              <li>• Oma kuljetus helpottaa keikan toteutusta.</li>
              <li>• Sopii häihin, yritysjuhliin, pikkujouluihin ja kesäjuhliin.</li>
              <li>• Saatavuus ja tarjouspyyntö hoituvat helposti verkossa.</li>
            </ul>
          </article>
        </section>

        {/* KAUPUNKI JA LOGISTIIKKA */}
        <section
          className="mt-12 rounded-3xl border border-joyridePurple/15 bg-gradient-to-br from-joyridePurple/10 to-joyrideCyan/10 p-7 shadow-xl dark:border-joyrideCyan/15"
          data-aos="fade-up"
        >
          <h2 className="font-lobster text-3xl font-bold text-joyridePurple dark:text-joyrideCyan">
            Bilebändi Joyride keikkailee kaupungissa {city} ja alueella {maakunta}
          </h2>

          <p className="mt-4 text-lg leading-relaxed font-light">
            {logisticsText}
          </p>

          <p className="mt-4 text-lg leading-relaxed font-light">
            Tarjouspyynnön yhteydessä tarkistetaan päivämäärä, tapahtumapaikka,
            soiton kesto, aikataulu ja mahdolliset lisätarpeet, kuten mikrofonin
            käyttö puheisiin tai taustamusiikin toisto bändin äänentoiston
            kautta.
          </p>
        </section>

        {/* FAQ */}
        <section className="mt-20" data-aos="fade-up">
          <h2 className="font-lobster text-4xl font-bold text-joyridePurple dark:text-joyrideCyan">
            Usein kysyttyä Bilebändi Joyriden keikoista kaupungissa {city}
          </h2>

          <div className="mt-7 grid gap-5">
            {defaultFaqs.map((faq) => (
              <article
                key={faq.question}
                className="rounded-3xl border border-joyridePurple/15 bg-white/85 p-6 shadow-lg dark:border-joyrideCyan/15 dark:bg-white/10"
              >
                <h3 className="text-xl font-bold text-joyridePurple dark:text-joyrideCyan">
                  {faq.question}
                </h3>

                <p className="mt-3 text-lg leading-relaxed font-light">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section
          className="mt-20 rounded-3xl bg-joyridePurple p-8 text-white shadow-2xl dark:bg-joyrideCyan dark:text-joyrideNavy sm:p-10"
          data-aos="fade-up"
        >
          <h2 className="font-lobster text-4xl font-bold">
            Haluatko Bilebändi Joyriden juhliisi kaupunkiin {city}?
          </h2>

          <p className="mt-4 max-w-3xl text-lg leading-relaxed font-light">
            Tarkista vapaa ajankohta ja lähetä tarjouspyyntö helposti verkossa.
            Pelkkä tiedustelu ei vielä sido sinua mihinkään.
          </p>

          <div className="mt-7 flex flex-col gap-4 sm:flex-row">
            <a
              href={bookingLink}
              onClick={handleAvailabilityClick}
              className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-base font-bold text-joyridePurple shadow-lg transition hover:-translate-y-0.5 hover:bg-joyrideNavy hover:text-white dark:text-joyrideNavy dark:hover:bg-joyrideNavy dark:hover:text-white"
            >
              Tarkista saatavuus
            </a>

            <a
              href="mailto:kontakti@bilebandijoyride.com"
              onClick={handleEmailClick}
              className="inline-flex items-center justify-center rounded-full border border-white/70 px-7 py-3 text-base font-bold text-white transition hover:-translate-y-0.5 hover:bg-white hover:text-joyridePurple dark:border-joyrideNavy/50 dark:text-joyrideNavy dark:hover:bg-joyrideNavy dark:hover:text-white"
            >
              Ota yhteyttä
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}